import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useValidateCouponMutation } from "gql/generated.graphql";
import { Button } from "dash-ui";
import { useState } from "react";

type IPromoCodeInputProps = {
  onValidateCoupon: (stripeCouponId?: string) => void;
  subscriptionProductId: string;
};

// Infer the form type from the schema
type PromoCodeForm = z.infer<typeof promoCodeSchema>;

const promoCodeSchema = z.object({
  couponCode: z
    .string()
    .regex(/^[A-Za-z0-9]*$/, "Only letters and numbers are allowed"),
});

function PromoCodeInput({
  onValidateCoupon,
  subscriptionProductId,
}: IPromoCodeInputProps) {
  const {
    register,
    handleSubmit,
    formState: { touchedFields },
  } = useForm<PromoCodeForm>({
    resolver: zodResolver(promoCodeSchema),
  });

  const [mutation, { loading }] = useValidateCouponMutation();
  const [error, setError] = useState(false);

  function onSubmit({ couponCode }: PromoCodeForm) {
    mutation({
      variables: {
        args: {
          subscriptionProductId,
          userHasTrialed: false,
          couponCode,
        },
      },
      onCompleted: (data) => {
        const promoCode = data.validateCoupon.coupon?.code;
        if (!data.validateCoupon.isValid) setError(true);
        else setError(false);
        onValidateCoupon(promoCode ?? undefined);
      },
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-row gap-2 mt-2">
        <input
          {...register("couponCode")}
          className={`border-2 rounded-lg p-1.5 pl-2 w-48 ${
            error && touchedFields
              ? "outline-red-200 border-red-200"
              : "outline-sky-200 border-sky-200"
          }`}
          placeholder="Enter coupon here..."
          onInput={(e) => {
            e.currentTarget.value = e.currentTarget.value.toUpperCase();
          }}></input>
        <Button smplus disabled={loading} isLoading={loading}>
          Apply
        </Button>
      </div>
    </form>
  );
}

export default PromoCodeInput;
